import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal, { Eyebrow } from "@/components/Reveal";
import BrushDivider from "@/components/BrushDivider";

const DARK_PAINT = "https://images.unsplash.com/photo-1557264322-b44d383a2906?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA6MTJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbHV4dXJ5JTIwYWJzdHJhY3QlMjBwYWludCUyMGluayUyMHNwbGFzaCUyMHRleHR1cmV8ZW58MHx8fHwxNzkwNzI5ODI1fDA&ixlib=rb-4.1.0&q=85";
const RED_TEX = "https://images.unsplash.com/photo-1671494685095-68b5c92963e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxkYXJrJTIwcmVkJTIwY3JpbXNvbiUyMHBhaW50JTIwdGV4dHVyZSUyMGJhY2tncm91bmR8ZW58MHx8fDE3OTA3Mjk5NDZ8MA&ixlib=rb-4.1.0&q=85";
const EDIT_SUITE = "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHx2aWRlbyUyMGVkaXRpbmclMjB0aW1lbGluZSUyMHNvZnR3YXJlJTIwc2NyZWVufGVufDB8fHx8MTc5MDcyOTk0Nnww&ixlib=rb-4.1.0&q=85";

const VALUES = [
  { t: "Creativity first", d: "Every brief starts as a blank canvas, not a template library." },
  { t: "Attention to detail", d: "Kerning, colour values, export settings — the unglamorous parts are where quality lives." },
  { t: "Practical experience", d: "This studio exists because we spent years making real work for real projects." },
  { t: "Digital-first", d: "We design for where things actually live: feeds, servers, streams and screens." },
  { t: "Flexible scale", d: "One icon or a full identity. A single edit or a monthly retainer." },
  { t: "Ongoing partnerships", d: "The best work happens when we know your brand deeply. We build for the long term." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-24 sm:pt-28">
      <Seo title="About" description="Arsenal Artists grew out of the Arsenal ecosystem's own creative work — now a dedicated studio for creators, communities and businesses." path="/about" />
      <header className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <Eyebrow>ABOUT</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-balance max-w-3xl">
          Built by doing, <span className="text-arsenal">not by pitching.</span>
        </h1>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <div className="space-y-5 text-slate-300 leading-relaxed">
            <p className="text-lg text-white font-medium">
              Arsenal Artists was built from the same creative work behind the Arsenal ecosystem. What started with creating identities, graphics, interfaces and promotional material for our own projects became a dedicated creative studio available to everyone.
            </p>
            <p>
              We're not an agency that learned design from a textbook. Every service on this site exists because we've shipped it — logos that had to work as 32-pixel Discord icons, brand systems that had to survive a community of thousands, videos that had to hold attention in a feed.
            </p>
            <p>
              That background makes us comfortable with almost anyone: individuals, streamers, gaming communities, startups, established businesses. Gaming is one of our strengths — but your project gets your style, not ours.
            </p>
            <p className="text-slate-400">
              Premium work, no corporate theatre. Tell us what you need; we'll tell you honestly what it takes.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="grid grid-cols-2 gap-4">
            <img src={DARK_PAINT} alt="Dark abstract paint texture" loading="lazy" className="border border-white/10 aspect-[3/4] object-cover" />
            <img src={RED_TEX} alt="Crimson paint texture" loading="lazy" className="border border-white/10 aspect-[3/4] object-cover mt-10" />
            <img src={EDIT_SUITE} alt="Video editing timeline" loading="lazy" className="col-span-2 border border-white/10 aspect-[21/9] object-cover" />
          </div>
        </Reveal>
      </div>

      <BrushDivider />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <Reveal>
          <Eyebrow>HOW WE WORK</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">What you can count on.</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
          {VALUES.map((v, i) => (
            <Reveal key={v.t} delay={i * 0.05}>
              <div className="h-full bg-ink-surface p-7 hover:bg-ink-elevated transition-colors">
                <span className="font-mono text-[11px] text-arsenal">0{i + 1}</span>
                <h3 className="mt-3 font-display text-lg font-bold text-white">{v.t}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/5 bg-ink-surface/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Work with the studio.</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/start-a-project" data-testid="about-start-project" className="clip-btn inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-sm px-8 py-4 transition-colors">
              START A PROJECT <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/portfolio" data-testid="about-view-work" className="clip-btn inline-flex items-center gap-2 border border-white/20 hover:border-arsenal hover:text-arsenal text-white font-display font-bold tracking-widest text-sm px-8 py-4 transition-colors">
              VIEW OUR WORK
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
